import { AdminLayout } from "@/components/admin-layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  HelpCircle, 
  MessageSquare, 
  Book, 
  Video, 
  Mail, 
  Phone,
  AlertCircle,
  CheckCircle,
  Clock,
  Users
} from "lucide-react";

export default function AdminSupportPage() {
  const supportTickets = [
    {
      id: "TICKET-001",
      title: "User unable to connect Plaid account",
      status: "open",
      priority: "high",
      user: "john@example.com",
      created: "2025-01-20",
      category: "Integration"
    },
    {
      id: "TICKET-002", 
      title: "Export functionality not working",
      status: "in-progress",
      priority: "medium",
      user: "sarah@company.com",
      created: "2025-01-19",
      category: "Reports"
    },
    {
      id: "TICKET-003",
      title: "Question about subscription limits",
      status: "resolved",
      priority: "low",
      user: "mike@business.net",
      created: "2025-01-18",
      category: "Billing"
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case "open": return "bg-red-100 text-red-800";
      case "in-progress": return "bg-yellow-100 text-yellow-800";
      case "resolved": return "bg-green-100 text-green-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high": return "bg-red-100 text-red-800";
      case "medium": return "bg-yellow-100 text-yellow-800";
      case "low": return "bg-green-100 text-green-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <AdminLayout>
      <div className="container mx-auto p-6 space-y-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Support Center</h1>
            <p className="text-muted-foreground">
              Manage customer support tickets and resources
            </p>
          </div>
        </div>

        {/* Support Stats */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardContent className="flex items-center p-6">
              <AlertCircle className="h-8 w-8 text-red-500" />
              <div className="ml-4">
                <p className="text-2xl font-bold">3</p>
                <p className="text-xs text-muted-foreground">Open Tickets</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="flex items-center p-6">
              <Clock className="h-8 w-8 text-yellow-500" />
              <div className="ml-4">
                <p className="text-2xl font-bold">2</p>
                <p className="text-xs text-muted-foreground">In Progress</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="flex items-center p-6">
              <CheckCircle className="h-8 w-8 text-green-500" />
              <div className="ml-4">
                <p className="text-2xl font-bold">15</p>
                <p className="text-xs text-muted-foreground">Resolved Today</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="flex items-center p-6">
              <Users className="h-8 w-8 text-blue-500" />
              <div className="ml-4">
                <p className="text-2xl font-bold">98%</p>
                <p className="text-xs text-muted-foreground">Satisfaction</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Recent Support Tickets */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Recent Support Tickets</CardTitle>
                <CardDescription>
                  Latest customer support requests and their status
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {supportTickets.map((ticket) => (
                    <div
                      key={ticket.id}
                      className="flex items-center justify-between p-4 border rounded-lg"
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{ticket.id}</span>
                          <Badge className={getPriorityColor(ticket.priority)}>
                            {ticket.priority}
                          </Badge>
                        </div>
                        <p className="text-sm font-medium">{ticket.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {ticket.user} • {ticket.category} • {ticket.created}
                        </p>
                      </div>
                      <Badge className={getStatusColor(ticket.status)}>
                        {ticket.status}
                      </Badge>
                    </div>
                  ))}
                </div>
                <div className="mt-4">
                  <Button variant="outline" className="w-full">
                    View All Tickets
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
                <CardDescription>
                  Common support and administrative tasks
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button variant="outline" className="w-full justify-start">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Create New Ticket
                </Button>
                
                <Button variant="outline" className="w-full justify-start">
                  <HelpCircle className="h-4 w-4 mr-2" />
                  Knowledge Base
                </Button>
                
                <Button variant="outline" className="w-full justify-start">
                  <Book className="h-4 w-4 mr-2" />
                  Documentation
                </Button>
                
                <Button variant="outline" className="w-full justify-start">
                  <Video className="h-4 w-4 mr-2" />
                  Video Tutorials
                </Button>
                
                <Button variant="outline" className="w-full justify-start">
                  <Mail className="h-4 w-4 mr-2" />
                  Email Templates
                </Button>
                
                <Button variant="outline" className="w-full justify-start">
                  <Phone className="h-4 w-4 mr-2" />
                  Call Center Queue
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Support Resources */}
        <Card>
          <CardHeader>
            <CardTitle>Support Resources</CardTitle>
            <CardDescription>
              Tools and information for customer support
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <h4 className="font-semibold">Common Issues</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Plaid connection failures</li>
                  <li>• Report generation errors</li>
                  <li>• Subscription billing questions</li>
                  <li>• Account setup assistance</li>
                </ul>
              </div>
              
              <div className="space-y-2">
                <h4 className="font-semibold">Escalation Contacts</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Technical: tech@zentrobooks.com</li>
                  <li>• Billing: billing@zentrobooks.com</li>
                  <li>• Security: security@zentrobooks.com</li>
                  <li>• Management: admin@zentrobooks.com</li>
                </ul>
              </div>
              
              <div className="space-y-2">
                <h4 className="font-semibold">SLA Targets</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• High Priority: 2 hours</li>
                  <li>• Medium Priority: 8 hours</li>
                  <li>• Low Priority: 24 hours</li>
                  <li>• Resolution: 48 hours</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}